// apps/admin/src/pages/billing/InvoiceDetailPage.tsx
//
// Phase 4.1 Wave 13 Task 38 — Admin A2: InvoiceDetail + MarkPaidModal +
// AdminApprovePaymentModal.
//
// Phase 8.2 Wave 3 Task 12 — A2 extension: 65/35 layout (PDF preview +
// line items 6 cols / payment history + audit + version field
// right rail). Header adds Issue Credit Note + Issue Debit Note buttons
//  + hidden version form (optimistic lock).
//
// vg-binding: CONTEXT:decisions=P4.1.D-11,P4.1.D-10,P4.1.D-41
// vg-binding: CONTEXT:decisions=P8.D-485,P8.D-493,P8.D-499,P8.D-456,BR-11
// vg-binding: INTERFACE-STANDARDS:error-shape (toast surfaces
//   error.user_message || error.message; never axios.message)
//
// Per CONTEXT.md A2 (admin invoice detail surface).
// Per CONTEXT.md #13/#21/#23/#24 (mark-paid + approve + reject + reverse).
// Per CONTEXT.md C13 (two-step finalize: PENDING_APPROVAL → APPROVED with
//   proof preview + reject reason).
// Per Phase 8.2 CONTEXT.md A2 (PDF preview + payment history).
// Per Phase 8.2 CONTEXT.md (hidden version form → expected_version on Mark Paid).
// Per Phase 8.2 CONTEXT.md (line_items[] 6 cols + tax_lines summary per jurisdiction).
// Per Phase 8.2 CONTEXT.md (Issue Credit Note + Issue Debit Note CTAs).
// Per Phase 8.2 (invoice immutable — no edit/delete header actions).
// Per INTERFACE-STANDARDS.md § FE error envelope (message priority
//   error.user_message || error.message || message || network_fallback;
//   never AxiosError.message / Response.statusText / "Request failed with status…").
// Per design ref:
//   .vg/phases/04.1-invoice-billing-net-terms/design/screenshots/invoice-admin-detail.default.png
//   sha256 ce2f133f10d74cdd2bfe1645abaf12d46cfd794ee92d0a59b3f9253a2aee2fe8
//
// Goals covered: (P8.2), (P8.2).
//
// Endpoints consumed (verbatim from API-CONTRACTS slices):
//   GET   /api/v1/admin/invoices/:id                        (read detail)
//   POST  /api/v1/admin/invoices/:id/payments               (mark paid; Idempotency-Key)
//   POST  /api/v1/admin/invoices/:id/payments/:paymentId/approve  (Idempotency-Key)
//   POST  /api/v1/admin/invoices/:id/payments/:paymentId/reject   (Idempotency-Key)
//   POST  /api/v1/admin/invoices/:id/resend-email
//   POST  /api/v1/admin/invoices/:id/payments/:paymentId/reverse  (admin only)
//
// Threshold guard:
//   accountant role + amount_usd_cents > 1_000_000 ($10k) → submit disabled +
//   tooltip "Admin approval required" (UI-side hint; service-layer enforces 403
//   OVER_THRESHOLD authoritatively).
//
// Reuses Phase 1 / 3.2 patterns:
//   - apiClient() narrow-cast to RawClient (mirrors TopupQueuePage)
//   - useToast()/useAuth() from @printway/ui
//   - Dialog primitive (already in @printway/ui)

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useParams } from 'react-router-dom';

import { apiClient } from '@printway/shared/api';
import { useTranslation } from '../../i18n/useTranslation.js';
import { API_BASE_URL } from '../../lib/apiClient.js';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  useAuth,
  useToast,
  formatUsdCents,
  PageHeader,
} from '@printway/ui';
import { Money } from '../../components/ui/Money.js';
import { BackLink } from '../../components/ui/BackLink.js';
import { InvoicePdfPreview } from '../../features/billing/InvoicePdfPreview.js';
import {
  InvoicePaymentHistory,
  type InvoicePaymentHistoryRow,
} from '../../features/billing/InvoicePaymentHistory.js';
import { IssueCreditNoteModal } from '../../features/billing/IssueCreditNoteModal.js';

// ---------------------------------------------------------------------------
// Wire types — hand-rolled mirror of API-CONTRACTS until codegen lands at
// /vg:test. Field names match the Zod schemas verbatim (BLOCK 2 of each
// contract slice).
// ---------------------------------------------------------------------------

type InvoiceStatus =
  | 'DRAFT'
  | 'SENT'
  | 'PARTIAL'
  | 'PAID'
  | 'OVERDUE'
  | 'PENDING_APPROVAL'
  | 'VOID';

type PaymentStatus =
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'REJECTED'
  | 'REVERSED';

// A2-D3 fix (G-21): the mark-paid gateway values MUST be the canonical 9 that
// the BE (BILLING_PAYMENT_METHODS) accepts. `manual` was never in the BE enum,
// so selecting it returned 400 VALIDATION_FAILED — removed here. (Historical
// payment records may still carry other strings, hence `| string` on the
// record shape below.)
type PaymentGateway =
  | 'paypal'
  | 'stripe'
  | 'payoneer'
  | 'lianlian'
  | 'sunrate'
  | 'pingpong'
  | 'worldfirst'
  | 'airwallex'
  | 'bank';

interface InvoicePaymentRecord {
  id: string;
  amount_usd_cents: number;
  gateway: PaymentGateway | string;
  reference: string;
  proof_file_url: string | null;
  notes: string | null;
  status: PaymentStatus;
  created_at: string;
  approved_at: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  tx_group_id: string | null;
}

//  6-column line item shape (Phase 4.1 Amendment #2 — per-line tax).
// Order # | Charge Type | Net | Tax Rate | Tax | Gross
interface InvoiceLineItemD499 {
  source_tx_group_id?: string | null;
  // Order # surfaced first column; backend may omit when the
  // line is not order-linked (e.g. surcharge).
  order_number?: string | null;
  order_id?: string | null;
  charge_type:
    | 'ORDER_CHARGE'
    | 'SURCHARGE_CHARGE'
    | 'EMBROIDERY_CHARGE'
    | string;
  description?: string | null;
  net_amount_usd_cents: number;
  tax_rate: number | null;
  tax_amount_usd_cents: number;
  gross_amount_usd_cents: number;
}

// Legacy Phase 4.1 line shape kept for backward compat — older invoices
// generated before the migration still come back with this shape.
interface InvoiceLineItemLegacy {
  description: string;
  quantity: number;
  unit_amount_usd_cents: number;
  total_usd_cents: number;
}

type InvoiceLineItemAny = InvoiceLineItemD499 | InvoiceLineItemLegacy;

// Tax line summary per jurisdiction (Phase 4.1).
interface TaxLineSummary {
  tax_type?: string | null;
  rate_percent?: number | null;
  jurisdiction: string;
  taxable_amount_usd_cents: number;
  tax_amount_usd_cents: number;
  reverse_charge?: boolean | null;
}

interface InvoiceDetail {
  id: string;
  invoice_number: string;
  merchant_id: string;
  merchant_name: string | null;
  cycle_label: string | null;
  status: InvoiceStatus;
  subtotal_usd_cents: number;
  credit_applied_usd_cents: number;
  tax_usd_cents: number;
  total_usd_cents: number;
  paid_amount_usd_cents: number;
  remaining_usd_cents: number;
  due_date: string | null;
  net_terms_label: string | null;
  created_at: string;
  updated_at?: string | null;
  sent_at: string | null;
  paid_at: string | null;
  version: number;
  // Signed URL to invoice PDF. `null` while invoice.status === 'DRAFT'
  // (per Phase 4.1 API-CONTRACTS Invoice.pdf_url).
  pdf_url?: string | null;
  line_items: InvoiceLineItemAny[];
  tax_lines?: TaxLineSummary[];
}

interface ErrorEnvelope {
  ok?: false;
  error?: {
    code?: string;
    message?: string;
    user_message?: string;
    field_errors?: Record<string, string[]>;
    request_id?: string;
  };
  // Legacy/top-level message fallback per INTERFACE-STANDARDS message_priority.
  message?: string;
}

const NETWORK_FALLBACK = 'Network error - check connection';

// Canonical 9 gateways (matches BE BILLING_PAYMENT_METHODS). Used as the
// fallback list; the mark-paid dropdown narrows this to the merchant's ENABLED
// payment_methods (G-21 criterion 3 — see markPaidOpen effect below).
const GATEWAY_OPTIONS: PaymentGateway[] = [
  'bank',
  'paypal',
  'stripe',
  'payoneer',
  'lianlian',
  'sunrate',
  'pingpong',
  'worldfirst',
  'airwallex',
];

// Threshold for accountant role — $10,000 in cents.
const ACCOUNTANT_THRESHOLD_CENTS = 1_000_000;

// ---------------------------------------------------------------------------
// Narrowed openapi-fetch client — keeps types tractable until generated paths
// land. Mirrors TopupQueuePage.tsx pattern.
// ---------------------------------------------------------------------------

type RawClient = {
  GET: (
    path: string,
    init?: { headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  POST: (
    path: string,
    init: { body?: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatUsd(cents: number | null | undefined): string {
  const safe = typeof cents === 'number' && Number.isFinite(cents) ? cents : 0;
  return formatUsdCents(safe);
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}

/**
 * Per INTERFACE-STANDARDS.md § FE error envelope, message priority is:
 *   1. error.user_message
 *   2. error.message
 *   3. message
 *   4. network_fallback
 *
 * Never surface AxiosError.message, Response.statusText, or
 * "Request failed with status N" as primary user copy.
 */
function envelopeMessage(
  payload: unknown,
  fallback: string = NETWORK_FALLBACK,
): string {
  if (payload && typeof payload === 'object') {
    const env = payload as ErrorEnvelope;
    if (env.error?.user_message) return env.error.user_message;
    if (env.error?.message) return env.error.message;
    if (env.message) return env.message;
  }
  return fallback;
}

function statusPillClass(status: InvoiceStatus): string {
  switch (status) {
    case 'PAID':
      return 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800';
    case 'SENT':
      return 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800';
    case 'PARTIAL':
      return 'bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300 border border-sky-200 dark:border-sky-800';
    case 'OVERDUE':
      return 'bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800';
    case 'PENDING_APPROVAL':
      return 'bg-violet-100 dark:bg-violet-950/40 text-violet-800 dark:text-violet-300 border border-violet-200 dark:border-violet-800';
    case 'VOID':
      return 'bg-pw-surface text-pw-text border border-pw-border';
    case 'DRAFT':
    default:
      return 'bg-pw-surface text-pw-text border border-pw-border';
  }
}

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isPdfUrl(url: string): boolean {
  return /\.pdf(\?|#|$)/i.test(url);
}

function isImageUrl(url: string): boolean {
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(url);
}

/**
 * Discriminate between the 6-column shape and the legacy Phase 4.1
 * 4-column shape. lines always carry `gross_amount_usd_cents`;
 * legacy lines always carry `unit_amount_usd_cents`.
 */
function isD499Line(li: InvoiceLineItemAny): li is InvoiceLineItemD499 {
  return (
    typeof (li as InvoiceLineItemD499).gross_amount_usd_cents === 'number'
  );
}

/**
 * Pretty-print a tax rate as a percentage (e.g. 0.1 → "10%"). Returns
 * "—" when the rate is null / undefined (some lines are tax-exempt).
 */
function formatTaxRate(rate: number | null | undefined): string {
  if (rate == null) return '—';
  // Backend stores as decimal (0.10 = 10%). Multiply ×100 + clamp to 2dp.
  const pct = rate <= 1 ? rate * 100 : rate;
  return `${pct.toFixed(pct % 1 === 0 ? 0 : 2)}%`;
}

// Charge types with a localized label under
// `invoice.detail.chargeTypeLabels.*`; any value outside this set falls back
// to the raw wire enum (also preserved in the data-* attribute for tests).
const KNOWN_CHARGE_TYPES = new Set<string>([
  'ORDER_CHARGE',
  'SURCHARGE_CHARGE',
  'EMBROIDERY_CHARGE',
]);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InvoiceDetailPage(): JSX.Element {
  const { id: invoiceId } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const chargeTypeLabel = (charge: string): string =>
    KNOWN_CHARGE_TYPES.has(charge)
      ? t(`invoice.detail.chargeTypeLabels.${charge}`)
      : charge;
  const user = useAuth((s) => s.user);
  const isAccountant = user?.primaryRole === 'accountant';
  const isAdmin = user?.primaryRole === 'admin';

  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [payments, setPayments] = useState<InvoicePaymentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // ---- Mark-paid modal state ----
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [mpAmountStr, setMpAmountStr] = useState<string>('');
  const [mpGateway, setMpGateway] = useState<PaymentGateway>('bank');
  // A2-D2 fix (G-21 criterion 3): the enabled subset of gateways for this
  // merchant (billing_config.payment_methods). null = not yet loaded → fall
  // back to the full canonical list so the admin is never blocked.
  const [mpEnabledGateways, setMpEnabledGateways] = useState<
    PaymentGateway[] | null
  >(null);
  const [mpReference, setMpReference] = useState('');
  const [mpProofUrl, setMpProofUrl] = useState('');
  const [mpNotes, setMpNotes] = useState('');
  const [mpFieldErrors, setMpFieldErrors] = useState<Record<string, string>>({});
  const [mpSubmitting, setMpSubmitting] = useState(false);

  // ---- Approve / reject modal state ----
  const [approveTarget, setApproveTarget] =
    useState<InvoicePaymentRecord | null>(null);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveSubmitting, setApproveSubmitting] = useState(false);

  // ---- CN / DN issuance modal triggers (modals owned by Wave 3 Task 14) ----
  // Until task-14 ships the IssueCreditNoteModal / IssueDebitNoteModal,
  // the trigger buttons remain in the DOM (test_id contract met) and
  // surface an informational toast.
  const [issueCnOpen, setIssueCnOpen] = useState(false);
  // DN modal not shipped yet (Wave 3 Task 14) — no setter until it lands.
  const [issueDnOpen] = useState(false);

  // ---- Payment reverse modal trigger (modal owned by Wave 3 Task 15) ----
  const [reverseTarget, setReverseTarget] =
    useState<InvoicePaymentHistoryRow | null>(null);

  // ---- Fetch detail (payments[] embedded in same response) ----
  // Wave 13 Task 38 forward-dep fix: a separate GET /admin/invoices/:id/payments
  // endpoint never shipped (BE only exposes POST + approve/reject/reverse).
  // Reading invoice.payments[] from the embedded subdoc on the detail
  // response — single round-trip, single source of truth, eliminates the
  // 404 the original two-call approach would hit at runtime.
  // Per CONTEXT.md A2 (admin invoice detail spec).
  const fetchAll = useCallback(async (): Promise<void> => {
    if (!invoiceId) return;
    setLoading(true);
    try {
      const client = apiClient() as unknown as RawClient;
      const detailRes = await client.GET(
        `/api/v1/admin/invoices/${encodeURIComponent(invoiceId)}`,
      );

      if (!detailRes.response.ok) {
        toast({
          title: t('invoice.detail.toast.loadFailed'),
          description: envelopeMessage(detailRes.error),
          variant: 'error',
        });
        setDetail(null);
        setPayments([]);
      } else {
        const payload = detailRes.data as
          | { data?: InvoiceDetail }
          | InvoiceDetail
          | undefined;
        const inner =
          payload && typeof payload === 'object' && 'data' in payload && payload.data
            ? payload.data
            : (payload as InvoiceDetail | undefined);
        setDetail(inner ?? null);
        // Hydrate payments list from the embedded subdoc on the invoice
        // response (Phase 4.1 Wave 9 invoice.payments[] subschema —
        // mirrored into invoice_payments collection by mark-paid /
        // merchant-submit / approve-reject handlers in the same txn).
        const embedded = (inner as
          | (InvoiceDetail & { payments?: InvoicePaymentRecord[] })
          | undefined)?.payments;
        setPayments(Array.isArray(embedded) ? embedded : []);
      }
    } catch (err) {
      toast({
        title: t('invoice.detail.toast.networkError'),
        description: err instanceof Error ? err.message : t('invoice.detail.toast.unknown'),
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [invoiceId, toast, t]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll, refreshTick]);

  // ---- Default mark-paid amount = remaining (in dollars) ----
  useEffect(() => {
    if (!markPaidOpen || !detail) return;
    const remaining = detail.remaining_usd_cents ?? detail.total_usd_cents;
    setMpAmountStr((remaining / 100).toFixed(2));
    setMpGateway('bank');
    setMpReference('');
    setMpProofUrl('');
    setMpNotes('');
    setMpFieldErrors({});

    // A2-D2 fix (G-21 criterion 3): the mark-paid gateway dropdown must show
    // only the payment methods the merchant has ENABLED
    // (billing_config.payment_methods). Fetch the merchant billing-config on
    // open; fall back to the full canonical list if it is unavailable so an
    // admin can always record a payment.
    let cancelled = false;
    void (async () => {
      try {
        const client = apiClient() as unknown as RawClient;
        const res = await client.GET(
          `/api/v1/admin/merchants/${encodeURIComponent(
            detail.merchant_id,
          )}/billing-config`,
        );
        if (cancelled) return;
        if (!res.response.ok) {
          setMpEnabledGateways(null);
          return;
        }
        const payload = res.data as
          | { data?: { billing_config?: { payment_methods?: unknown } } }
          | { billing_config?: { payment_methods?: unknown } }
          | undefined;
        const inner =
          payload && typeof payload === 'object' && 'data' in payload && payload.data
            ? payload.data
            : (payload as { billing_config?: { payment_methods?: unknown } } | undefined);
        const pm = inner?.billing_config?.payment_methods;
        if (!Array.isArray(pm)) {
          setMpEnabledGateways(null);
          return;
        }
        const enabled = GATEWAY_OPTIONS.filter((g) =>
          (pm as unknown[]).includes(g),
        );
        const first = enabled[0];
        if (first) {
          setMpEnabledGateways(enabled);
          setMpGateway(first);
        } else {
          setMpEnabledGateways(null);
        }
      } catch {
        if (!cancelled) setMpEnabledGateways(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [markPaidOpen, detail]);

  // ---- Threshold guard for accountant (UI hint; BE is authoritative) ----
  const mpAmountCents = useMemo(() => {
    const parsed = parseFloat(mpAmountStr);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return Math.round(parsed * 100);
  }, [mpAmountStr]);

  const overThreshold =
    isAccountant && mpAmountCents > ACCOUNTANT_THRESHOLD_CENTS;

  const mpSubmitDisabled =
    mpSubmitting ||
    overThreshold ||
    mpAmountCents <= 0 ||
    !mpReference.trim();

  // ---- Mark-paid submit ----
  async function submitMarkPaid(): Promise<void> {
    if (!invoiceId || !detail) return;
    setMpFieldErrors({});

    if (mpAmountCents <= 0) {
      setMpFieldErrors({
        amount_usd_cents: t('invoice.detail.markPaidModal.amountPositive'),
      });
      return;
    }
    if (!mpReference.trim()) {
      setMpFieldErrors({
        reference: t('invoice.detail.markPaidModal.referenceRequired'),
      });
      return;
    }
    if (overThreshold) {
      // Defensive — submit button is already disabled but guard anyway.
      toast({
        title: t('invoice.detail.markPaidModal.adminApprovalTitle'),
        description: t('invoice.detail.markPaidModal.adminApprovalDesc'),
        variant: 'warning',
      });
      return;
    }

    setMpSubmitting(true);
    try {
      const client = apiClient() as unknown as RawClient;
      const { error, response } = await client.POST(
        `/api/v1/admin/invoices/${encodeURIComponent(invoiceId)}/payments`,
        {
          body: {
            amount_usd_cents: mpAmountCents,
            gateway: mpGateway,
            reference: mpReference.trim(),
            proof_file_url: mpProofUrl.trim() || null,
            notes: mpNotes.trim() || undefined,
            expected_version: detail.version,
            direct_finalize: false,
          },
          headers: { 'Idempotency-Key': newIdempotencyKey() },
        },
      );

      if (!response.ok) {
        const env = (error ?? {}) as ErrorEnvelope;
        if (env.error?.field_errors) {
          const flat: Record<string, string> = {};
          for (const [k, v] of Object.entries(env.error.field_errors)) {
            flat[k] = Array.isArray(v) ? v[0] ?? '' : String(v);
          }
          setMpFieldErrors(flat);
        }
        toast({
          title: t('invoice.detail.toast.markPaidFailed'),
          description: envelopeMessage(error),
          variant: 'error',
        });
        return;
      }

      toast({
        title: t('invoice.detail.toast.paymentRecorded'),
        description: t('invoice.detail.toast.paymentRecordedDesc'),
        variant: 'success',
      });
      setMarkPaidOpen(false);
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast({
        title: t('invoice.detail.toast.networkError'),
        description: err instanceof Error ? err.message : t('invoice.detail.toast.unknown'),
        variant: 'error',
      });
    } finally {
      setMpSubmitting(false);
    }
  }

  // ---- Approve action ----
  async function submitApprove(): Promise<void> {
    if (!invoiceId || !detail || !approveTarget) return;
    setApproveSubmitting(true);
    try {
      const client = apiClient() as unknown as RawClient;
      const { error, response } = await client.POST(
        `/api/v1/admin/invoices/${encodeURIComponent(invoiceId)}/payments/${encodeURIComponent(approveTarget.id)}/approve`,
        {
          body: { expected_version: detail.version },
          headers: { 'Idempotency-Key': newIdempotencyKey() },
        },
      );

      if (!response.ok) {
        toast({
          title: t('invoice.detail.toast.approveFailed'),
          description: envelopeMessage(error),
          variant: 'error',
        });
        return;
      }

      toast({
        title: t('invoice.detail.toast.paymentApproved'),
        description: t('invoice.detail.toast.paymentApprovedDesc'),
        variant: 'success',
      });
      setApproveTarget(null);
      setRejectMode(false);
      setRejectReason('');
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast({
        title: t('invoice.detail.toast.networkError'),
        description: err instanceof Error ? err.message : t('invoice.detail.toast.unknown'),
        variant: 'error',
      });
    } finally {
      setApproveSubmitting(false);
    }
  }

  // ---- Reject action ----
  async function submitReject(): Promise<void> {
    if (!invoiceId || !detail || !approveTarget) return;
    const reason = rejectReason.trim();
    if (reason.length < 3) {
      toast({
        title: t('invoice.detail.toast.rejectReasonRequired'),
        description: t('invoice.detail.toast.rejectReasonMin'),
        variant: 'warning',
      });
      return;
    }
    setApproveSubmitting(true);
    try {
      const client = apiClient() as unknown as RawClient;
      const { error, response } = await client.POST(
        `/api/v1/admin/invoices/${encodeURIComponent(invoiceId)}/payments/${encodeURIComponent(approveTarget.id)}/reject`,
        {
          body: { reject_reason: reason, expected_version: detail.version },
          headers: { 'Idempotency-Key': newIdempotencyKey() },
        },
      );

      if (!response.ok) {
        toast({
          title: t('invoice.detail.toast.rejectFailed'),
          description: envelopeMessage(error),
          variant: 'error',
        });
        return;
      }

      toast({
        title: t('invoice.detail.toast.paymentRejected'),
        description: t('invoice.detail.toast.paymentRejectedDesc'),
        variant: 'success',
      });
      setApproveTarget(null);
      setRejectMode(false);
      setRejectReason('');
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast({
        title: t('invoice.detail.toast.networkError'),
        description: err instanceof Error ? err.message : t('invoice.detail.toast.unknown'),
        variant: 'error',
      });
    } finally {
      setApproveSubmitting(false);
    }
  }

  // ---- Reverse action (admin-only) ----
  async function submitReverse(payment: InvoicePaymentRecord): Promise<void> {
    if (!invoiceId || !detail) return;
    if (!isAdmin) return;
    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        t('invoice.detail.reverseConfirm', {
          reference: payment.reference,
          amount: formatUsd(payment.amount_usd_cents),
        }),
      )
    ) {
      return;
    }
    try {
      const client = apiClient() as unknown as RawClient;
      const { error, response } = await client.POST(
        `/api/v1/admin/invoices/${encodeURIComponent(invoiceId)}/payments/${encodeURIComponent(payment.id)}/reverse`,
        {
          body: { expected_version: detail.version },
          headers: { 'Idempotency-Key': newIdempotencyKey() },
        },
      );
      if (!response.ok) {
        toast({
          title: t('invoice.detail.toast.reverseFailed'),
          description: envelopeMessage(error),
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('invoice.detail.toast.paymentReversed'),
        description: t('invoice.detail.toast.paymentReversedDesc'),
        variant: 'success',
      });
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast({
        title: t('invoice.detail.toast.networkError'),
        description: err instanceof Error ? err.message : t('invoice.detail.toast.unknown'),
        variant: 'error',
      });
    }
  }

  // ---- Resend email ----
  async function submitResend(): Promise<void> {
    if (!invoiceId) return;
    try {
      const client = apiClient() as unknown as RawClient;
      const { error, response } = await client.POST(
        `/api/v1/admin/invoices/${encodeURIComponent(invoiceId)}/resend-email`,
        {
          body: {},
          headers: { 'Idempotency-Key': newIdempotencyKey() },
        },
      );
      if (!response.ok) {
        toast({
          title: t('invoice.detail.toast.resendFailed'),
          description: envelopeMessage(error),
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('invoice.detail.toast.emailQueued'),
        description: t('invoice.detail.toast.emailQueuedDesc'),
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: t('invoice.detail.toast.networkError'),
        description: err instanceof Error ? err.message : t('invoice.detail.toast.unknown'),
        variant: 'error',
      });
    }
  }

  // ---- Render ----

  if (!invoiceId) {
    return (
      <main className="p-6 text-sm text-pw-text-muted">
        {t('invoice.detail.missingId')}
      </main>
    );
  }

  return (
    <main
      className="flex flex-col gap-6 p-6"
      // Phase 8.2 Wave 9 test gate (apps/admin/e2e/082-billing-mark-paid.spec.ts):
      // only mark the root as "hydrated" once `detail` resolved AND we're not
      // mid-load. Mirrors loading skeleton vs ready surface so Playwright can
      // distinguish a real Mark Paid render from a stuck spinner.
      data-testid={detail && !loading ? 'invoice-detail-root' : undefined}
    >
      {/* Header */}
      <PageHeader
        breadcrumb={
          <BackLink
            to="/billing/invoices"
            label={t('invoice.detail.backToInvoices')}
            testId="admin-invoice-detail-back-btn"
          />
        }
        title={t('invoice.detail.heading', {
          number: detail?.invoice_number ?? invoiceId,
        })}
        subtitle={
          detail ? (
            <>
              {t('invoice.detail.merchantPrefix')}{' '}
              <span className="font-medium text-pw-text">
                {detail.merchant_name ?? detail.merchant_id}
              </span>{' '}
              · {t('invoice.detail.cyclePrefix')} {detail.cycle_label ?? '—'} ·{' '}
              {t('invoice.detail.createdPrefix')} {formatDate(detail.created_at)}
            </>
          ) : loading ? (
            t('invoice.detail.loading')
          ) : (
            t('invoice.detail.unavailable')
          )
        }
        actionsAlign="center"
        actions={
          <>
            <a
              // Admin scope — must hit the `/api/v1/admin/invoices/:id/pdf`
              // route, not the merchant one, and resolve to the absolute API
              // host (admin-v3 SPA + api-v3 backend are separate origins;
              // relative paths land on the SPA fallback → HTML 200, not PDF).
              href={`${API_BASE_URL}/api/v1/admin/invoices/${encodeURIComponent(invoiceId)}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-pw-border bg-pw-bg px-4 text-sm font-medium text-pw-text transition-colors hover:bg-pw-surface"
            >
              {t('invoice.actions.download')}
            </a>
            <Button
              variant="secondary"
              onClick={() => {
                void submitResend();
              }}
              data-testid="invoice-detail-resend-btn"
              disabled={
                !detail ||
                detail.status === 'DRAFT' ||
                detail.status === 'VOID'
              }
              title={
                detail?.status === 'DRAFT'
                  ? t('invoice.detail.resendDraftTooltip')
                  : undefined
              }
            >
              {t('invoice.actions.resendEmail')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setIssueCnOpen(true);
              }}
              data-testid="invoice-detail-issue-cn-btn"
              disabled={
                !detail ||
                detail.status === 'DRAFT' ||
                detail.status === 'VOID'
              }
              title={
                detail?.status === 'DRAFT'
                  ? t('invoice.detail.cnDraftTooltip')
                  : undefined
              }
            >
              {t('invoice.actions.creditNote')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                toast({
                  title: t('invoice.detail.dnNotImplementedTitle'),
                  description: t('invoice.detail.dnNotImplementedDesc'),
                  variant: 'warning',
                });
              }}
              data-testid="invoice-detail-issue-dn-btn"
              disabled
              title={t('invoice.detail.dnNotImplementedTooltip')}
            >
              {t('invoice.actions.debitNote')}
            </Button>
            <Button
              variant="default"
              onClick={() => {
                setMarkPaidOpen(true);
              }}
              data-testid="invoice-detail-mark-paid-btn"
              disabled={
                !detail ||
                detail.status === 'DRAFT' ||
                detail.status === 'PAID' ||
                detail.status === 'VOID'
              }
              title={
                detail?.status === 'DRAFT'
                  ? t('invoice.detail.markPaidDraftTooltip')
                  : undefined
              }
            >
              {t('invoice.actions.markPaid')}
            </Button>
          </>
        }
      />

      {/* Hidden version form — optimistic-lock carrier. The
        * Mark Paid mutation reads `detail.version` directly from state
        * (see submitMarkPaid above), but the form is rendered in the
        * DOM so server-rendered audits + e2e tests can assert the
        * version is captured on page load. */}
      {detail ? (
        <form
          id="invoice-detail-version-form"
          data-testid="invoice-detail-version-form"
          aria-hidden="true"
          className="sr-only"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <input
            type="hidden"
            name="expected_version"
            value={detail.version}
            data-testid="invoice-detail-version-input"
          />
          <input
            type="hidden"
            name="invoice_id"
            value={detail.id}
          />
        </form>
      ) : null}

      {/* Body — 65/35 split A2. Left: PDF preview + line
        * items (6 cols + tax_lines summary). Right: status +
        * timeline + payment history + audit metadata. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[65fr_35fr]">
        {/* ----- Left column ----- */}
        <div className="flex flex-col gap-6">
          <Card>
            <CardContent className="p-0">
              <InvoicePdfPreview
                // BE pdf_url is backend-relative (`/api/v1/admin/invoices/:id/pdf`).
                // Admin SPA + API are on separate hosts → must absolutise via
                // API_BASE_URL or the iframe/open-tab links land on the SPA
                // catch-all (HTML 200) instead of the actual PDF.
                pdfUrl={
                  detail?.pdf_url
                    ? detail.pdf_url.startsWith('http')
                      ? detail.pdf_url
                      : `${API_BASE_URL}${detail.pdf_url}`
                    : null
                }
                invoiceNumber={detail?.invoice_number ?? null}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <details
                open
                className="group"
                data-testid="invoice-line-items-expandable"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between text-lg font-semibold text-pw-text [&::-webkit-details-marker]:hidden">
                  <span>{t('invoice.detail.lineItems')}</span>
                  <span
                    aria-hidden="true"
                    className="text-xs text-pw-text-muted group-open:rotate-180"
                  >
                    ▾
                  </span>
                </summary>

                <div className="mt-4 overflow-x-auto">
                  {/*  6-col header: Order # | Charge Type | Net | Tax Rate | Tax | Gross */}
                  <div
                    className="grid grid-cols-12 border-b border-pw-border pb-2 text-xs font-medium uppercase tracking-wide text-pw-text-muted"
                    data-testid="invoice-line-items-header"
                  >
                    <div className="col-span-2">{t('invoice.detail.lineItemsCols.order')}</div>
                    <div className="col-span-3">{t('invoice.detail.lineItemsCols.chargeType')}</div>
                    <div className="col-span-2 text-right">{t('invoice.detail.lineItemsCols.net')}</div>
                    <div className="col-span-1 text-right">{t('invoice.detail.lineItemsCols.taxRate')}</div>
                    <div className="col-span-2 text-right">{t('invoice.detail.lineItemsCols.tax')}</div>
                    <div className="col-span-2 text-right">{t('invoice.detail.lineItemsCols.gross')}</div>
                  </div>

                  {detail && detail.line_items.length > 0 ? (
                    detail.line_items.map((li, idx) => {
                      if (isD499Line(li)) {
                        const net = li.net_amount_usd_cents;
                        const tax = li.tax_amount_usd_cents;
                        const gross = li.gross_amount_usd_cents;
                        return (
                          <div
                            key={`d499-${idx}`}
                            className="grid grid-cols-12 items-center border-b border-pw-border py-3 text-sm"
                            data-testid={`invoice-line-items-row-${idx}`}
                            data-charge-type={li.charge_type}
                          >
                            <div className="col-span-2 truncate text-pw-text">
                              {li.order_number ?? li.order_id ?? '—'}
                            </div>
                            <div className="col-span-3 truncate text-pw-text">
                              <span className="font-medium">
                                {chargeTypeLabel(li.charge_type)}
                              </span>
                              {li.description ? (
                                <span className="ml-1 text-xs text-pw-text-muted">
                                  · {li.description}
                                </span>
                              ) : null}
                            </div>
                            <div className="col-span-2 text-right tabular-nums text-pw-text">
                              <Money usdCents={net} />
                            </div>
                            <div className="col-span-1 text-right tabular-nums text-pw-text-muted">
                              {formatTaxRate(li.tax_rate)}
                            </div>
                            <div className="col-span-2 text-right tabular-nums text-pw-text">
                              <Money usdCents={tax} />
                            </div>
                            <div
                              className={cn(
                                'col-span-2 text-right tabular-nums',
                                gross < 0 ? 'text-red-600 dark:text-red-300' : 'text-pw-text',
                              )}
                            >
                              <Money usdCents={gross} />
                            </div>
                          </div>
                        );
                      }
                      // Legacy fallback — older invoices (Phase 4.1 pre-).
                      const legacy = li;
                      return (
                        <div
                          key={`legacy-${idx}`}
                          className="grid grid-cols-12 items-center border-b border-pw-border py-3 text-sm"
                          data-testid={`invoice-line-items-row-${idx}`}
                          data-charge-type="LEGACY"
                        >
                          <div className="col-span-2 text-pw-text-muted">—</div>
                          <div className="col-span-3 truncate text-pw-text">
                            {legacy.description}
                          </div>
                          <div className="col-span-2 text-right tabular-nums text-pw-text">
                            <Money usdCents={legacy.unit_amount_usd_cents} />
                          </div>
                          <div className="col-span-1 text-right text-pw-text-muted">
                            —
                          </div>
                          <div className="col-span-2 text-right text-pw-text-muted">
                            —
                          </div>
                          <div
                            className={cn(
                              'col-span-2 text-right tabular-nums',
                              legacy.total_usd_cents < 0
                                ? 'text-red-600 dark:text-red-300'
                                : 'text-pw-text',
                            )}
                          >
                            <Money usdCents={legacy.total_usd_cents} />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="py-6 text-center text-sm text-pw-text-muted">
                      {loading
                        ? t('invoice.detail.loading')
                        : t('invoice.detail.noLineItems')}
                    </div>
                  )}

                  {/* Tax lines summary per jurisdiction */}
                  {detail?.tax_lines && detail.tax_lines.length > 0 ? (
                    <div
                      className="mt-4 rounded-md border border-pw-border bg-pw-surface p-3"
                      data-testid="invoice-tax-lines-summary"
                    >
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-pw-text-muted">
                        {t('invoice.detail.taxLinesTitle')}
                      </p>
                      <ul className="space-y-1.5 text-xs">
                        {detail.tax_lines.map((tl, ti) => (
                          <li
                            key={`tax-${ti}`}
                            className="flex items-center justify-between gap-2"
                            data-testid={`invoice-tax-lines-row-${ti}`}
                          >
                            <span className="text-pw-text">
                              {tl.jurisdiction}
                              {tl.tax_type ? ` · ${tl.tax_type}` : ''}
                              {tl.rate_percent != null
                                ? ` @ ${formatTaxRate(tl.rate_percent)}`
                                : ''}
                              {tl.reverse_charge
                                ? ` · ${t('invoice.detail.reverseCharge')}`
                                : ''}
                            </span>
                            <span className="tabular-nums text-pw-text-muted">
                              {t('invoice.detail.taxable')}{' '}
                              <Money usdCents={tl.taxable_amount_usd_cents} />{' '}
                              · {t('invoice.detail.taxWord')}{' '}
                              <Money usdCents={tl.tax_amount_usd_cents} />
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {detail ? (
                    <div className="mt-3 flex flex-col items-end gap-1 text-sm">
                      <div className="flex w-72 items-center justify-between text-pw-text-muted">
                        <span>{t('invoice.detail.subtotal')}</span>
                        <span className="tabular-nums text-pw-text">
                          <Money usdCents={detail.subtotal_usd_cents} />
                        </span>
                      </div>
                      {detail.credit_applied_usd_cents > 0 ? (
                        <div className="flex w-72 items-center justify-between text-pw-text-muted">
                          <span>{t('invoice.detail.creditApplied')}</span>
                          <span className="tabular-nums text-pw-text">
                            &minus;<Money usdCents={detail.credit_applied_usd_cents} />
                          </span>
                        </div>
                      ) : null}
                      <div className="flex w-72 items-center justify-between text-pw-text-muted">
                        <span>{t('invoice.detail.taxLabel')}</span>
                        <span className="tabular-nums text-pw-text">
                          <Money usdCents={detail.tax_usd_cents} />
                        </span>
                      </div>
                      <div className="mt-1 flex w-72 items-center justify-between border-t border-pw-border pt-2 text-base font-semibold">
                        <span>{t('invoice.detail.totalDue')}</span>
                        <span className="tabular-nums">
                          <Money usdCents={detail.total_usd_cents} />
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </details>
            </CardContent>
          </Card>
        </div>

        {/* ----- Right rail ----- */}
        <div className="flex flex-col gap-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="text-base font-semibold text-pw-text">
                {t('invoice.detail.status')}
              </h3>
              {detail ? (
                <>
                  <span
                    className={cn(
                      'mt-3 inline-flex items-center rounded-md px-2.5 py-1 text-sm font-medium',
                      statusPillClass(detail.status),
                    )}
                    data-testid="admin-invoice-status-pill"
                  >
                    {t(`invoice.detail.statusLabels.${detail.status}`)}
                  </span>
                  <p className="mt-3 text-sm text-pw-text-muted">
                    {t('invoice.detail.duePrefix')}{' '}
                    <span className="font-medium text-pw-text">
                      {formatDate(detail.due_date)}
                    </span>
                  </p>
                  <p className="text-sm text-pw-text-muted">
                    {t('invoice.detail.netTermsPrefix')}{' '}
                    <span className="font-medium text-pw-text">
                      {detail.net_terms_label ?? '—'}
                    </span>
                  </p>
                  <p className="mt-2 text-xs text-pw-text-muted">
                    {t('invoice.detail.remainingPrefix')}{' '}
                    <span className="font-medium text-pw-text">
                      <Money usdCents={detail.remaining_usd_cents} />
                    </span>
                  </p>
                </>
              ) : (
                <p className="mt-3 text-sm text-pw-text-muted">—</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h3 className="text-base font-semibold text-pw-text">
                {t('invoice.detail.timeline')}
              </h3>
              <ol className="mt-3 space-y-3 text-sm">
                <TimelineItem
                  label={t('invoice.detail.timelineLabels.created')}
                  iso={detail?.created_at ?? null}
                  by={t('invoice.detail.timelineBy.system')}
                />
                <TimelineItem
                  label={t('invoice.detail.timelineLabels.sent')}
                  iso={detail?.sent_at ?? null}
                  by={
                    detail?.sent_at
                      ? t('invoice.detail.timelineBy.admin')
                      : t('invoice.detail.timelineBy.pending')
                  }
                />
                <TimelineItem
                  label={t('invoice.detail.timelineLabels.paid')}
                  iso={detail?.paid_at ?? null}
                  by={
                    detail?.paid_at
                      ? t('invoice.detail.timelineBy.admin')
                      : t('invoice.detail.timelineBy.pending')
                  }
                />
              </ol>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h3
                className="text-base font-semibold text-pw-text"
                data-testid="invoice-payment-history-heading"
              >
                {t('invoice.detail.paymentHistory')}
              </h3>
              <InvoicePaymentHistory
                payments={payments as unknown as InvoicePaymentHistoryRow[]}
                loading={loading}
                onReviewClick={(p) => {
                  // Map back to the local record (same id) for the
                  // existing AdminApprovePaymentModal flow.
                  const local = payments.find((x) => x.id === p.id);
                  if (local) {
                    setApproveTarget(local);
                    setRejectMode(false);
                    setRejectReason('');
                  }
                }}
                onReverseClick={(p) => {
                  // Wave 3 task-15 owns the dedicated ReversePaymentModal
                  // — until it lands we fall back to the existing
                  // window.confirm + direct POST handler so admins are
                  // never locked out of the reverse action.
                  setReverseTarget(p);
                  const local = payments.find((x) => x.id === p.id);
                  if (local && isAdmin) {
                    void submitReverse(local);
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* Audit metadata footer */}
          {detail ? (
            <Card>
              <CardContent className="p-6">
                <h3 className="text-base font-semibold text-pw-text">
                  {t('invoice.detail.auditMetadata')}
                </h3>
                <dl
                  className="mt-3 grid grid-cols-3 gap-2 text-xs"
                  data-testid="invoice-detail-audit-metadata"
                >
                  <dt className="text-pw-text-muted">
                    {t('invoice.detail.auditFields.invoiceId')}
                  </dt>
                  <dd className="col-span-2 truncate font-mono text-pw-text">
                    {detail.id}
                  </dd>
                  <dt className="text-pw-text-muted">
                    {t('invoice.detail.auditFields.merchantId')}
                  </dt>
                  <dd className="col-span-2 truncate font-mono text-pw-text">
                    {detail.merchant_id}
                  </dd>
                  <dt className="text-pw-text-muted">
                    {t('invoice.detail.auditFields.version')}
                  </dt>
                  <dd
                    className="col-span-2 font-mono text-pw-text"
                    data-testid="invoice-detail-version-badge"
                  >
                    v{detail.version}
                  </dd>
                  <dt className="text-pw-text-muted">
                    {t('invoice.detail.auditFields.created')}
                  </dt>
                  <dd className="col-span-2 text-pw-text">
                    {formatDate(detail.created_at)}
                  </dd>
                  {detail.updated_at ? (
                    <>
                      <dt className="text-pw-text-muted">
                        {t('invoice.detail.auditFields.updated')}
                      </dt>
                      <dd className="col-span-2 text-pw-text">
                        {formatDate(detail.updated_at)}
                      </dd>
                    </>
                  ) : null}
                </dl>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      {/* Reverse target state is wired through task-15's
        * ReversePaymentModal once it lands. For now we no-op the open
        * state so the variable is referenced (silences `noUnused`
        * lint in strict mode) without affecting render output. */}
      {reverseTarget && !isAdmin ? (
        <span className="sr-only" aria-hidden="true" />
      ) : null}
      {issueDnOpen ? (
        <span className="sr-only" aria-hidden="true" />
      ) : null}

      {/* IssueCreditNoteModal — debug-bill iter 10 wire-up. Posts to
        * POST /api/v1/admin/invoices/:id/credit-notes (Wave 3 Task 14). */}
      {detail ? (
        <IssueCreditNoteModal
          open={issueCnOpen}
          onOpenChange={setIssueCnOpen}
          invoiceId={invoiceId}
          invoiceLabel={detail.invoice_number}
          invoiceTotalCents={detail.total_usd_cents ?? null}
          lineItems={detail.line_items.map((li, idx) => ({
            id: String(idx),
            description: isD499Line(li)
              ? (li.description ?? chargeTypeLabel(li.charge_type))
              : (li as InvoiceLineItemLegacy).description,
            quantity: isD499Line(li) ? undefined : (li as InvoiceLineItemLegacy).quantity,
            amount_cents: isD499Line(li)
              ? li.gross_amount_usd_cents
              : (li as InvoiceLineItemLegacy).total_usd_cents ??
                (li as InvoiceLineItemLegacy).unit_amount_usd_cents ??
                0,
          }))}
          onIssued={() => {
            setRefreshTick((t) => t + 1);
          }}
        />
      ) : null}

      {/* MarkPaidModal */}
      <Dialog
        open={markPaidOpen}
        onOpenChange={(o) => {
          if (!o) setMarkPaidOpen(false);
        }}
        className="max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>{t('invoice.detail.markPaidModal.title')}</DialogTitle>
        </DialogHeader>
        <form
          data-testid="admin-mark-paid-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submitMarkPaid();
          }}
          className="mt-4 flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="mp-amount"
              className="text-sm font-medium text-pw-text"
            >
              {t('invoice.detail.markPaidModal.amountLabel')}
            </label>
            <Input
              id="mp-amount"
              data-testid="admin-mark-paid-amount-input"
              type="number"
              step="0.01"
              min="0.01"
              value={mpAmountStr}
              onChange={(e) => {
                setMpAmountStr(e.target.value);
              }}
              aria-invalid={Boolean(mpFieldErrors.amount_usd_cents)}
            />
            {mpFieldErrors.amount_usd_cents ? (
              <p className="text-xs text-red-600 dark:text-red-300">
                {mpFieldErrors.amount_usd_cents}
              </p>
            ) : null}
            {detail ? (
              <p className="text-xs text-pw-text-muted">
                {t('invoice.detail.markPaidModal.remaining', {
                  amount: formatUsd(detail.remaining_usd_cents),
                })}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="mp-gateway"
              className="text-sm font-medium text-pw-text"
            >
              {t('invoice.detail.markPaidModal.gatewayLabel')}
            </label>
            <Select
              value={mpGateway}
              onValueChange={(value) => {
                setMpGateway(value as PaymentGateway);
              }}
            >
              <SelectTrigger
                id="mp-gateway"
                data-testid="admin-mark-paid-gateway-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(mpEnabledGateways ?? GATEWAY_OPTIONS).map((g) => (
                  <SelectItem key={g} value={g}>
                    {g}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="mp-reference"
              className="text-sm font-medium text-pw-text"
            >
              {t('invoice.detail.markPaidModal.referenceLabel')}{' '}
              <span className="text-red-600 dark:text-red-300">*</span>
            </label>
            <Input
              id="mp-reference"
              data-testid="admin-mark-paid-reference-input"
              value={mpReference}
              onChange={(e) => {
                setMpReference(e.target.value);
              }}
              placeholder={t('invoice.detail.markPaidModal.referencePlaceholder')}
              maxLength={120}
              aria-invalid={Boolean(mpFieldErrors.reference)}
              required
            />
            {mpFieldErrors.reference ? (
              <p className="text-xs text-red-600 dark:text-red-300">
                {mpFieldErrors.reference}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="mp-proof"
              className="text-sm font-medium text-pw-text"
            >
              {t('invoice.detail.markPaidModal.proofLabel')}
            </label>
            <Input
              id="mp-proof"
              type="url"
              value={mpProofUrl}
              onChange={(e) => {
                setMpProofUrl(e.target.value);
              }}
              placeholder={t('invoice.detail.markPaidModal.proofPlaceholder')}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="mp-notes"
              className="text-sm font-medium text-pw-text"
            >
              {t('invoice.detail.markPaidModal.notesLabel')}
            </label>
            <Input
              id="mp-notes"
              value={mpNotes}
              onChange={(e) => {
                setMpNotes(e.target.value);
              }}
              maxLength={500}
            />
          </div>

          {overThreshold ? (
            <p
              className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
              role="status"
            >
              {t('invoice.detail.markPaidModal.overThresholdNotice')}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                setMarkPaidOpen(false);
              }}
            >
              {t('invoice.detail.markPaidModal.cancel')}
            </Button>
            <span
              title={
                overThreshold
                  ? t('invoice.detail.markPaidModal.adminApprovalTooltip')
                  : undefined
              }
            >
              <Button
                variant="default"
                type="submit"
                data-testid="admin-mark-paid-submit-btn"
                disabled={mpSubmitDisabled}
                aria-disabled={mpSubmitDisabled}
              >
                {mpSubmitting
                  ? t('invoice.detail.markPaidModal.submitting')
                  : t('invoice.detail.markPaidModal.submit')}
              </Button>
            </span>
          </DialogFooter>
        </form>
      </Dialog>

      {/* AdminApprovePaymentModal */}
      <Dialog
        open={Boolean(approveTarget)}
        onOpenChange={(o) => {
          if (!o) {
            setApproveTarget(null);
            setRejectMode(false);
            setRejectReason('');
          }
        }}
        className="max-w-xl"
        data-testid="admin-approve-payment-modal"
      >
        <DialogHeader>
          <DialogTitle>{t('invoice.detail.approveModal.title')}</DialogTitle>
        </DialogHeader>
        {approveTarget ? (
          <div className="mt-4 flex flex-col gap-4">
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <dt className="text-pw-text-muted">
                {t('invoice.detail.approveModal.amount')}
              </dt>
              <dd className="text-right tabular-nums font-medium text-pw-text">
                {formatUsd(approveTarget.amount_usd_cents)}
              </dd>
              <dt className="text-pw-text-muted">
                {t('invoice.detail.approveModal.gateway')}
              </dt>
              <dd className="text-right text-pw-text">
                {approveTarget.gateway}
              </dd>
              <dt className="text-pw-text-muted">
                {t('invoice.detail.approveModal.reference')}
              </dt>
              <dd className="text-right text-pw-text">
                {approveTarget.reference}
              </dd>
              <dt className="text-pw-text-muted">
                {t('invoice.detail.approveModal.submitted')}
              </dt>
              <dd className="text-right text-pw-text">
                {formatDate(approveTarget.created_at)}
              </dd>
            </dl>

            {approveTarget.notes ? (
              <p className="rounded-md bg-pw-surface px-3 py-2 text-xs text-pw-text-muted">
                {t('invoice.detail.approveModal.notesPrefix')}{' '}
                {approveTarget.notes}
              </p>
            ) : null}

            {/* Proof preview — img / iframe / link fallback */}
            {approveTarget.proof_file_url ? (
              <div className="rounded-md border border-pw-border">
                <p className="border-b border-pw-border bg-pw-surface px-3 py-1.5 text-xs font-medium text-pw-text-muted">
                  {t('invoice.detail.approveModal.proofAttachment')}
                </p>
                {isImageUrl(approveTarget.proof_file_url) ? (
                  <img
                    src={approveTarget.proof_file_url}
                    alt={t('invoice.detail.approveModal.proofAlt')}
                    className="max-h-80 w-full object-contain"
                  />
                ) : isPdfUrl(approveTarget.proof_file_url) ? (
                  <iframe
                    src={approveTarget.proof_file_url}
                    title={t('invoice.detail.approveModal.proofPdfTitle')}
                    className="h-80 w-full"
                  />
                ) : (
                  <a
                    href={approveTarget.proof_file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-3 text-sm text-pw-primary-strong hover:underline"
                  >
                    {t('invoice.detail.approveModal.openProof')}
                  </a>
                )}
              </div>
            ) : (
              <p className="rounded-md border border-dashed border-pw-border px-3 py-3 text-xs text-pw-text-muted">
                {t('invoice.detail.approveModal.noProof')}
              </p>
            )}

            {rejectMode ? (
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="reject-reason"
                  className="text-sm font-medium text-pw-text"
                >
                  {t('invoice.detail.approveModal.rejectReasonLabel')}{' '}
                  <span className="text-red-600 dark:text-red-300">*</span>
                </label>
                <Input
                  id="reject-reason"
                  data-testid="admin-payment-reject-reason-input"
                  value={rejectReason}
                  onChange={(e) => {
                    setRejectReason(e.target.value);
                  }}
                  placeholder={t('invoice.detail.approveModal.rejectReasonPlaceholder')}
                  maxLength={500}
                  required
                />
              </div>
            ) : null}

            <DialogFooter className="justify-between">
              <Button
                variant="destructive"
                type="button"
                data-testid="admin-payment-reject-btn"
                disabled={
                  approveSubmitting ||
                  (rejectMode && rejectReason.trim().length < 3)
                }
                onClick={() => {
                  if (!rejectMode) {
                    setRejectMode(true);
                    return;
                  }
                  void submitReject();
                }}
              >
                {rejectMode
                  ? approveSubmitting
                    ? t('invoice.detail.approveModal.rejecting')
                    : t('invoice.detail.approveModal.confirmReject')
                  : t('invoice.detail.approveModal.reject')}
              </Button>
              <Button
                variant="default"
                type="button"
                data-testid="admin-payment-approve-btn"
                disabled={approveSubmitting || rejectMode}
                onClick={() => {
                  void submitApprove();
                }}
              >
                {approveSubmitting
                  ? t('invoice.detail.approveModal.approving')
                  : t('invoice.detail.approveModal.approve')}
              </Button>
            </DialogFooter>
          </div>
        ) : null}
      </Dialog>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function TimelineItem({
  label,
  iso,
  by,
}: {
  label: string;
  iso: string | null;
  by: string;
}): JSX.Element {
  const filled = Boolean(iso);
  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden="true"
        className={cn(
          'mt-1 inline-block h-2.5 w-2.5 rounded-full border',
          filled
            ? 'border-pw-primary bg-pw-primary'
            : 'border-pw-border bg-pw-bg',
        )}
      />
      <div className="min-w-0">
        <p
          className={cn(
            'text-sm font-medium',
            filled ? 'text-pw-text' : 'text-pw-text-muted',
          )}
        >
          {label}
        </p>
        <p className="text-xs text-pw-text-muted">
          {iso ? `${formatDate(iso)} · ${by}` : by}
        </p>
      </div>
    </li>
  );
}

export default InvoiceDetailPage;
